import { useMemo } from "react";
import type { FlowSection, Question, OriginalFormState } from "../types";

export interface TrackedChanges {
	formSettings: {
		formTitleChanged: boolean;
		autoApproveChanged: boolean;
		themeChanged: boolean;
		statusChanged: boolean;
	};
	tags: {
		changed: boolean;
		currentIds: string[];
	};
	questions: {
		created: Question[];
		updated: Question[];
		deleted: string[];
	};
	pageAttributes: {
		sectionId: string;
		attributeKey: string;
		oldValue: string;
		newValue: string;
	}[];
	pageUpdates: {
		sectionId: string;
		status: "enabled" | "disabled";
	}[];
	fieldUpdates: {
		sectionId: string;
		fieldId: string;
		changes: {
			label?: string;
			placeholder?: string;
		};
	}[];
}

interface UseChangeTrackerOptions {
	formTitle: string;
	sections: FlowSection[];
	questions: Question[];
	autoApproveTestimonials: boolean;
	selectedTagIds: string[];
	theme: "light" | "dark" | "system-default";
	status: "published" | "unpublished";
	originalState: OriginalFormState;
}

export function useChangeTracker({ formTitle, sections, questions, autoApproveTestimonials, selectedTagIds, theme, status, originalState }: UseChangeTrackerOptions) {
	const trackedChanges = useMemo((): TrackedChanges => {
		// Track form settings
		const formSettings = {
			formTitleChanged: formTitle !== originalState.formTitle,
			autoApproveChanged: autoApproveTestimonials !== originalState.autoApproveTestimonials,
			themeChanged: theme !== originalState.theme,
			statusChanged: status !== originalState.status,
		};

		// Track tag changes
		const originalTagSet = new Set(originalState.selectedTagIds);
		const tagsChanged = originalState.selectedTagIds.length !== selectedTagIds.length || selectedTagIds.some((id) => !originalTagSet.has(id));

		// Track question changes
		const originalQuestionMap = new Map(originalState.questions.map((q) => [q.id, q]));
		const currentQuestionMap = new Map(questions.map((q) => [q.id, q]));

		const createdQuestions = questions.filter((q) => !originalQuestionMap.has(q.id));
		const deletedQuestionIds = originalState.questions.filter((q) => !currentQuestionMap.has(q.id)).map((q) => q.id);
		const updatedQuestions = questions.filter((q) => {
			const original = originalQuestionMap.get(q.id);
			if (!original) return false;
			// Normalize descriptions to compare properly (treat undefined and "" as equal)
			const currentDesc = q.description || "";
			const originalDesc = original.description || "";
			return (
				q.title !== original.title ||
				currentDesc !== originalDesc ||
				q.format !== original.format ||
				(q.video_option_label || "") !== (original.video_option_label || "") ||
				(q.video_option_description || "") !== (original.video_option_description || "") ||
				(q.audio_option_label || "") !== (original.audio_option_label || "") ||
				(q.audio_option_description || "") !== (original.audio_option_description || "") ||
				(q.text_option_label || "") !== (original.text_option_label || "") ||
				(q.text_option_description || "") !== (original.text_option_description || "")
			);
		});

		// Track page attribute changes (section title/description)
		const pageAttributes: TrackedChanges["pageAttributes"] = [];
		const pageUpdates: TrackedChanges["pageUpdates"] = [];
		const fieldUpdates: TrackedChanges["fieldUpdates"] = [];
		const originalSectionMap = new Map(originalState.sections.map((s) => [s.id, s]));

		for (const section of sections) {
			const original = originalSectionMap.get(section.id);
			if (!original) {
				continue;
			}

			if (section.title !== original.title) {
				pageAttributes.push({
					sectionId: section.id,
					attributeKey: "title",
					oldValue: original.title,
					newValue: section.title,
				});
			}

			if (section.description !== original.description) {
				pageAttributes.push({
					sectionId: section.id,
					attributeKey: "description",
					oldValue: original.description || "",
					newValue: section.description || "",
				});
			}

			// Normalize image_url values for comparison (treat null, undefined, and "" as equivalent)
			const currentImageUrl = section.image_url || null;
			const originalImageUrl = original.image_url || null;

			if (currentImageUrl !== originalImageUrl) {
				pageAttributes.push({
					sectionId: section.id,
					attributeKey: "image_url",
					oldValue: originalImageUrl || "",
					newValue: currentImageUrl || "",
				});
			}

			// Track status (enabled/disabled) changes for toggleable sections (page-level update)
			if (section.canToggle && section.enabled !== original.enabled) {
				pageUpdates.push({
					sectionId: section.id,
					status: section.enabled ? "enabled" : "disabled",
				});
			}

			// Track field changes (label/placeholder)
			if (section.fields && original.fields) {
				const originalFieldMap = new Map(original.fields.map((f) => [f.id, f]));

				for (const field of section.fields) {
					const originalField = originalFieldMap.get(field.id);
					if (!originalField) continue;

					const changes: TrackedChanges["fieldUpdates"][0]["changes"] = {};
					if (field.label !== originalField.label) changes.label = field.label;
					if ((field.placeholder || "") !== (originalField.placeholder || "")) changes.placeholder = field.placeholder;

					if (Object.keys(changes).length > 0) {
						fieldUpdates.push({
							sectionId: section.id,
							fieldId: field.id,
							changes,
						});
					}
				}
			}
		}

		return {
			formSettings,
			tags: {
				changed: tagsChanged,
				currentIds: selectedTagIds,
			},
			questions: {
				created: createdQuestions,
				updated: updatedQuestions,
				deleted: deletedQuestionIds,
			},
			pageAttributes,
			pageUpdates,
			fieldUpdates,
		};
	}, [formTitle, sections, questions, autoApproveTestimonials, selectedTagIds, theme, status, originalState]);

	const hasChanges = useMemo(() => {
		return (
			trackedChanges.formSettings.formTitleChanged ||
			trackedChanges.formSettings.autoApproveChanged ||
			trackedChanges.formSettings.themeChanged ||
			trackedChanges.formSettings.statusChanged ||
			trackedChanges.tags.changed ||
			trackedChanges.questions.created.length > 0 ||
			trackedChanges.questions.updated.length > 0 ||
			trackedChanges.questions.deleted.length > 0 ||
			trackedChanges.pageAttributes.length > 0 ||
			trackedChanges.pageUpdates.length > 0 ||
			trackedChanges.fieldUpdates.length > 0
		);
	}, [trackedChanges]);

	return {
		trackedChanges,
		hasChanges,
	};
}
